// src/encounter/structured-note/note-date-line.tsx
//
// PROD-2947 · frame v2 ONLY · the 6 Oct note interaction contract, K7 (Date). A template date item as a note line, in
// place of the DS renderer's date field (a "Select a date" box inside a card — ENC-L10, G2):
//   · empty → the item's label and ONE quiet "Add", which opens the DS date picker;
//   · set   → the date as text (D-45, "5 Oct 2026") and a quiet "Edit", which opens the same picker;
//   · read-only (a provider signature, rule 38) → the date as text, or "Not asked".
// The answer written is what the DS renderer's date field wrote: a `Date` (the page's Risk bridge and the QR mapping
// already take it). A stored ISO string still reads (the bridge hydrates either).
import { buttonClasses, DatePicker, formatPickerDate, NoteLine } from '@scripsteam/scrips-signal-ds';
import { NOTE_DATE_FORMAT, toNoteDate } from './note-date-model';
import { NoteLabel } from './label-parts';

export interface NoteDateLineProps {
  /** The item's anchor (jump targets and the Sign list land here). */
  id: string;
  label: string;
  value: unknown;
  /** Absent, or `readOnly` → the date reads as text. */
  onChange?: (date: Date | undefined) => void;
  readOnly?: boolean;
  /** A date that cannot be in the future (an onset): the picker offers no later day. */
  notAfterToday?: boolean;
}

export function NoteDateLine({ id, label, value, onChange, readOnly = false, notAfterToday = false }: NoteDateLineProps) {
  const date = toNoteDate(value);
  if (readOnly || !onChange) {
    return (
      <div data-note-date={id}>
        <NoteLine label={<NoteLabel label={label} />} align="center">
          <span id={id} className={date ? 'font-semibold text-text-primary' : 'text-text-secondary'}>
            {date ? formatPickerDate(date, NOTE_DATE_FORMAT) : 'Not asked'}
          </span>
        </NoteLine>
      </div>
    );
  }
  const action = date ? 'Edit' : 'Add';
  return (
    <div data-note-date={id}>
      <NoteLine label={<NoteLabel label={label} />} align="center" valueClassName="flex flex-wrap items-center gap-2">
        {date && <span className="font-semibold text-text-primary">{formatPickerDate(date, NOTE_DATE_FORMAT)}</span>}
        <DatePicker
          // Inline beside the date text, not a full-width field row (the DS root defaults to w-full).
          className="w-auto"
          value={date}
          onChange={(d) => onChange(d)}
          dateFormat={NOTE_DATE_FORMAT}
          captionLayout="dropdown"
          {...(notAfterToday ? { maxDate: new Date() } : {})}
          trigger={
            // A host element, as the DS asks (its trigger swap merges the popover props onto it).
            <button type="button" id={id} aria-label={`${action} ${label}`} className={buttonClasses({ variant: 'tertiary', size: 'sm' })}>
              {action}
            </button>
          }
        />
      </NoteLine>
    </div>
  );
}
